import React from "react";
import { motion } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import {
  Hammer,
  Compass,
  Coins,
  UserCog,
  X,
  Check,
  Sparkles,
  Crosshair,
} from "lucide-react";

const DARK = "#0F2818";
const FOREST = "#14401F";
const LIME = "#B8D958";
const AMBER = "#C7913A";
const CREAM = "#F7F8EF";

// The orbit sectors — what we *could* invest in (we don't filter by these)
const ORBIT_SECTORS = [
  "SaaS",
  "D2C",
  "Logistics",
  "Manufacturing",
  "Agritech",
  "Climate",
  "Healthtech",
  "Mobility",
  "Fintech",
  "Deeptech",
  "Edtech",
  "Media",
];

// The 4 pattern-based filters we DO care about
const PATTERNS = [
  {
    n: "01",
    code: "BRK",
    icon: Hammer,
    title: "Broken Industries",
    one: "Friction-rich, paper-driven, sleepy categories.",
    desc: "Logistics, manufacturing, agri, retail — sectors where the playbook hasn't been re-written since liberalisation.",
    tags: ["First-principles", "Status-quo allergy", "Workflow rewire"],
  },
  {
    n: "02",
    code: "UND",
    icon: Compass,
    title: "Underserved Markets",
    one: "Customer bases nobody is building for.",
    desc: "Bharat-first audiences: Tier 2/3 cities, vernacular users, blue-collar workforces, MSME owners.",
    tags: ["Tier 2/3 native", "Vernacular", "Blue-collar"],
  },
  {
    n: "03",
    code: "CEF",
    icon: Coins,
    title: "Capital-Efficient Models",
    one: "Burn discipline as a moat.",
    desc: "Founders who validate fast, ship cheaply, and treat every rupee like it might be their last. PMF before pomp.",
    tags: ["PMF-first", "Lean teams", "Default-alive"],
  },
  {
    n: "04",
    code: "OPR",
    icon: UserCog,
    title: "Operator-Led Founders",
    one: "Lived the problem before pitching it.",
    desc: "Domain operators who left jobs to build solutions for problems they bled for. Insiders, not tourists.",
    tags: ["Domain insider", "Customer #1", "Pattern-recognition"],
  },
];

// What we explicitly DON'T filter on — the "anti-thesis"
const ANTI_FILTERS = [
  "Pedigree (IIT/IIM/Ivy)",
  "Metro-only HQs",
  "Sector preference",
  "Logo-chasing rounds",
  "Lead-VC consensus",
];

export default function InvestmentThesis() {
  return (
    <section
      id="thesis"
      data-testid="thesis-section"
      className="relative overflow-hidden bg-[#F4F2E7]"
    >
      {/* Paper grain background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.45]"
        style={{
          background:
            "radial-gradient(1px 1px at 20% 30%, rgba(15,40,24,0.07) 0, transparent 1px), radial-gradient(1px 1px at 60% 70%, rgba(15,40,24,0.06) 0, transparent 1px), radial-gradient(1px 1px at 40% 90%, rgba(15,40,24,0.06) 0, transparent 1px)",
          backgroundSize: "180px 180px",
        }}
      />
      {/* Subtle horizontal rules — newsprint feel */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(0deg, transparent 32px, #14401F 32px, #14401F 33px, transparent 33px)",
          backgroundSize: "100% 33px",
        }}
      />

      {/* Top ticker tape — sectors scrolling */}
      <div className="relative border-y border-[#14401F]/20 bg-[#0F2818] text-[#F7F8EF] overflow-hidden">
        <div className="flex gap-10 it-ticker py-2.5">
          {[...Array(2)].map((_, k) => (
            <React.Fragment key={k}>
              {ORBIT_SECTORS.map((s, i) => (
                <span
                  key={`${k}-${i}`}
                  className="shrink-0 inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.32em] font-medium text-[#F7F8EF]/70"
                >
                  <span className="text-[#B8D958]">◆</span>
                  {s}
                </span>
              ))}
              <span className="shrink-0 inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.32em] font-bold text-[#C7913A]">
                <span>✱</span>
                SECTOR AGNOSTIC
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        {/* Eyebrow + Title */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14 lg:mb-20">
          <div className="max-w-3xl">
            <Eyebrow>Investment Thesis · The SparkSeed Doctrine</Eyebrow>
            <h2
              className="font-serif-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight font-bold text-[#0F2818]"
              data-testid="thesis-title"
            >
              We back founders who turn{" "}
              <Italic className="text-[#14401F]">broken industries</Italic>{" "}
              into billion-rupee opportunities.
            </h2>
          </div>
          {/* Dossier stamp */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
            whileInView={{ opacity: 1, scale: 1, rotate: -8 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="shrink-0 self-start lg:self-end"
          >
            <div
              className="relative inline-flex flex-col items-center justify-center px-5 py-3 border-2 border-[#C7913A] rounded-md text-[#C7913A]"
              style={{ fontFamily: "var(--font-mono, ui-monospace, monospace)" }}
            >
              <span className="text-[9px] uppercase tracking-[0.3em] font-bold">
                Classified · Dossier
              </span>
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold mt-0.5">
                Doctrine v.2026.1
              </span>
              <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#C7913A]" />
            </div>
          </motion.div>
        </div>

        {/* The Compass — Sector Agnostic centerpiece */}
        <div className="relative mb-20 lg:mb-28">
          <SectorAgnosticCompass />
        </div>

        {/* Pattern over Sector — comparison panel */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 mb-20 lg:mb-28">
          {/* Anti-filter card */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 relative p-7 lg:p-8 rounded-3xl border-2 border-dashed border-[#0F2818]/20 bg-transparent"
            data-testid="anti-filter-card"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-[#0F2818]/55 mb-3">
              Most VCs filter on
            </p>
            <h3 className="font-serif-display text-2xl lg:text-3xl font-bold text-[#0F2818]/65 mb-5">
              The Usual Gatekeepers.
            </h3>
            <ul className="space-y-2.5">
              {ANTI_FILTERS.map((f) => (
                <li
                  key={f}
                  className="flex items-center gap-3 text-[#0F2818]/60"
                >
                  <span className="w-7 h-7 rounded-full border-2 border-[#0F2818]/25 flex items-center justify-center shrink-0">
                    <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>
                  <span className="text-sm line-through decoration-[1px] decoration-[#0F2818]/40">
                    {f}
                  </span>
                </li>
              ))}
            </ul>
            <div className="absolute -bottom-4 right-6">
              <div
                className="px-3 py-1.5 rounded-md text-[9px] font-mono font-bold uppercase tracking-[0.2em] bg-[#0F2818]/8 text-[#0F2818]/55 border border-[#0F2818]/15"
              >
                NOT US
              </div>
            </div>
          </motion.div>

          {/* Our filters */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7 relative p-7 lg:p-9 rounded-3xl bg-[#0F2818] text-[#F7F8EF] overflow-hidden"
            data-testid="our-filter-card"
          >
            {/* glowing dot */}
            <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(184,217,88,0.18), transparent 70%)",
              }}
            />
            <p className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-[#B8D958] mb-3">
              SparkSeed filters on
            </p>
            <h3 className="font-serif-display text-2xl lg:text-3xl font-bold text-[#F7F8EF] mb-5">
              Patterns. <span className="text-[#B8D958]">Not verticals.</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {PATTERNS.map((p) => (
                <div
                  key={p.n}
                  className="flex items-center gap-2.5 p-3 rounded-xl bg-[#14401F]/60 border border-[#B8D958]/15"
                >
                  <span className="w-7 h-7 rounded-full bg-[#B8D958] text-[#0F2818] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                  <span className="text-sm font-medium text-[#F7F8EF]">
                    {p.title}
                  </span>
                </div>
              ))}
            </div>
            <div className="absolute -bottom-4 left-6">
              <div className="px-3 py-1.5 rounded-md text-[9px] font-mono font-bold uppercase tracking-[0.2em] bg-[#B8D958] text-[#0F2818] border border-[#B8D958]">
                THIS IS US
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes it-ticker-move {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .it-ticker {
          animation: it-ticker-move 56s linear infinite;
          width: max-content;
        }
        .it-ticker:hover { animation-play-state: paused; }

        @keyframes it-orbit-rot {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .it-orbit {
          animation: it-orbit-rot 60s linear infinite;
        }
        .it-orbit-counter {
          animation: it-orbit-rot 60s linear infinite reverse;
        }
      `}</style>
    </section>
  );
}

// — The Compass (Sector Agnostic centerpiece) —
function SectorAgnosticCompass() {
  // Distribute sectors in a circle
  const N = ORBIT_SECTORS.length;
  return (
    <div className="relative w-full flex justify-center">
      {/* Wrapper */}
      <div className="relative w-[320px] h-[320px] sm:w-[420px] sm:h-[420px] lg:w-[560px] lg:h-[560px]">
        {/* Outer rings */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          aria-hidden
        >
          {/* Outer dashed ring */}
          <circle cx="50" cy="50" r="46" fill="none" stroke="#14401F" strokeOpacity="0.18" strokeWidth="0.25" strokeDasharray="0.6 1" />
          {/* Mid ring */}
          <circle cx="50" cy="50" r="36" fill="none" stroke="#14401F" strokeOpacity="0.12" strokeWidth="0.25" />
          {/* Inner ring */}
          <circle cx="50" cy="50" r="26" fill="none" stroke="#14401F" strokeOpacity="0.18" strokeWidth="0.3" strokeDasharray="2 2" />
          {/* Compass crosshair */}
          <line x1="50" y1="6" x2="50" y2="94" stroke="#14401F" strokeOpacity="0.08" strokeWidth="0.2" />
          <line x1="6" y1="50" x2="94" y2="50" stroke="#14401F" strokeOpacity="0.08" strokeWidth="0.2" />
        </svg>

        {/* Orbiting sector chips (rotating slowly) */}
        <div className="absolute inset-0 it-orbit">
          {ORBIT_SECTORS.map((s, i) => {
            const angle = (i / N) * 360;
            const rad = (angle * Math.PI) / 180;
            const r = 46; // % radius
            const x = 50 + r * Math.cos(rad);
            const y = 50 + r * Math.sin(rad);
            return (
              <div
                key={s}
                className="absolute it-orbit-counter"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <span className="block px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-full bg-white border border-[#0F2818]/15 text-[10px] lg:text-[11px] font-mono uppercase tracking-[0.18em] font-semibold text-[#0F2818] shadow-[0_2px_8px_-3px_rgba(15,40,24,0.18)] whitespace-nowrap">
                  {s}
                </span>
              </div>
            );
          })}
        </div>

        {/* Center medallion — SECTOR AGNOSTIC */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center"
        >
          <div className="relative w-44 h-44 sm:w-52 sm:h-52 lg:w-60 lg:h-60 rounded-full bg-gradient-to-br from-[#0F2818] to-[#14401F] text-[#F7F8EF] flex flex-col items-center justify-center shadow-[0_30px_60px_-30px_rgba(15,40,24,0.7)]">
            {/* Crosshair icon top */}
            <Crosshair className="w-5 h-5 text-[#B8D958] mb-2" strokeWidth={1.5} />
            <span className="text-[9px] uppercase tracking-[0.3em] font-mono font-bold text-[#B8D958]/85">
              The Core Tenet
            </span>
            <span className="font-serif-display text-[28px] sm:text-[32px] lg:text-[40px] leading-none font-bold mt-2.5 tracking-tight">
              Sector
            </span>
            <span className="font-serif-display text-[28px] sm:text-[32px] lg:text-[40px] leading-none italic font-bold text-[#B8D958] tracking-tight">
              Agnostic.
            </span>
            <span className="text-[9px] uppercase tracking-[0.22em] font-mono text-[#F7F8EF]/55 mt-3">
              00 · CENTERPIECE
            </span>
            {/* Decorative inner ring */}
            <span className="absolute inset-3 rounded-full border border-[#B8D958]/15" />
            {/* Pulsing dot */}
            <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C7913A] opacity-70" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#C7913A]" />
            </span>
          </div>
          {/* Caption under medallion */}
          <p className="mt-6 max-w-xs lg:max-w-sm text-sm text-[#0F2818]/75 leading-relaxed text-center font-medium px-4">
            We don&apos;t pick winners by industry. We pick them by the size of
            the <em className="text-[#14401F] font-semibold">broken thing</em>{" "}
            they&apos;re solving.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

// — Editorial broadsheet pillar row —
function PatternRow({ p, idx }) {
  const Icon = p.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: idx * 0.08 }}
      data-testid={`thesis-pillar-${p.n}`}
      className="group relative grid grid-cols-12 gap-3 lg:gap-6 py-7 lg:py-9 border-b border-[#0F2818]/15 hover:bg-[#0F2818]/[0.025] transition-colors"
    >
      {/* Numeral */}
      <div className="col-span-2 lg:col-span-1 flex items-start">
        <span className="font-serif-display text-4xl lg:text-6xl font-bold text-[#0F2818] leading-none tabular-nums tracking-tighter group-hover:text-[#14401F] transition-colors">
          {p.n}
        </span>
      </div>

      {/* Title + meta */}
      <div className="col-span-10 lg:col-span-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="px-2 py-0.5 rounded-md bg-[#14401F] text-[#B8D958] text-[10px] font-mono font-bold uppercase tracking-[0.18em]">
            {p.code}
          </span>
          <Icon className="w-4 h-4 text-[#14401F]" strokeWidth={1.8} />
        </div>
        <h3 className="font-serif-display text-2xl lg:text-3xl font-bold text-[#0F2818] leading-tight mb-2">
          {p.title}
        </h3>
        <p className="text-[#14401F]/75 italic text-sm lg:text-base font-medium leading-snug">
          {p.one}
        </p>
      </div>

      {/* Body */}
      <div className="col-span-12 lg:col-span-5 lg:pl-4 lg:border-l lg:border-[#0F2818]/15">
        <p className="text-sm lg:text-base text-[#0F2818]/80 leading-relaxed">
          {p.desc}
        </p>
      </div>

      {/* Tags */}
      <div className="col-span-12 lg:col-span-2 flex lg:flex-col items-start gap-1.5 flex-wrap">
        {p.tags.map((t) => (
          <span
            key={t}
            className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md text-[#14401F] border border-[#14401F]/20 bg-[#F7F8EF]"
          >
            {t}
          </span>
        ))}
      </div>

      {/* Hover indicator arrow */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-12 rounded-l-full opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: idx % 2 === 0 ? LIME : AMBER }}
      />
    </motion.div>
  );
}
